import type { Tool, ToolPostPayload } from "@repo/app-types/entities/tool";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import type { FC } from "react";
import { Dialog } from "react-aria-components";
import {
  useGetToolQuery,
  useUpdateToolMutation,
} from "../../../api/entities/tool.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { ToolEditorSlideoverDialogContent } from "./toolEditorSlideoverDialogContent.js";

export interface ToolEditorSlideoverDialogProps {
  toolId: string;
  onRemoveFromAgent: (toolId: string) => void;
  onAfterSave?: (updatedTool: Tool) => Promise<void>;
}

export const ToolEditorSlideoverDialog: FC<ToolEditorSlideoverDialogProps> = (
  props,
) => {
  const { data, isLoading, isError } = useGetToolQuery(props.toolId);

  const dispatch = useAppDispatch();

  const [updateTool, { reset: resetUpdate }] = useUpdateToolMutation();

  const handleRemoveFromAgent = (toolId: string, close: () => void) => {
    props.onRemoveFromAgent(toolId);
    close();
  };

  const handleSave = async (payload: ToolPostPayload, close: () => void) => {
    try {
      const { tool: updatedTool } = await updateTool({
        toolId: props.toolId,
        payload,
      }).unwrap();

      showNotification(dispatch, {
        id: `tool-updated-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tool updated",
        secondaryMessage:
          "Tool has been updated successfully. Publishing changes...",
      });

      try {
        await props.onAfterSave?.(updatedTool);
      } catch (error) {
        console.error("Failed to publish after saving tool:", error);
      }

      close();
    } catch (error) {
      showNotification(dispatch, {
        id: `tool-update-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to update tool",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps the user's changes
      throw error;
    } finally {
      // The arguments can hold secret values; don't leave them in the store.
      resetUpdate();
    }
  };

  const title = data ? `Edit ${data.tool.name}` : "Edit tool";

  return (
    <Dialog className="isolate flex h-full flex-col">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={isLoading}>
            {title}
          </SlideoverDialogTitleHeader>

          {!data && isLoading ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <DefaultSkeleton />
              </div>
            </SlideoverDialogContent>
          ) : null}

          {!data && !isLoading && isError ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <p className="text-sm text-red-500">
                  Failed to load tool. Please try again.
                </p>
              </div>
            </SlideoverDialogContent>
          ) : null}

          {data ? (
            // Keyed by tool, so unsaved edits never carry over if the
            // slideover is pointed at another tool without closing.
            <ToolEditorSlideoverDialogContent
              key={data.tool.id}
              tool={data.tool}
              secrets={data.secrets}
              onRemoveFromAgent={(toolId) =>
                handleRemoveFromAgent(toolId, close)
              }
              onCancel={close}
              onSave={(payload) => handleSave(payload, close)}
            />
          ) : null}
        </>
      )}
    </Dialog>
  );
};
